<template>
  <div
    class="guest-row flex items-center justify-between px-30 overflow-hidden select-none cursor-pointer mobile:px-20"
    :class="item.snapshotChange"
    :style="{ height: itemHeight + 'px' }"
    @mouseenter="idState.showGuestActions = true"
    @mouseover="idState.showGuestActions = true"
    @mouseleave="idState.showGuestActions = false"
    @touchend.prevent="onTouchEnd"
    @touchmove="touchDragging = true"
    @click="item.snapshotChange !== 'removed' ? $emit('guest-update', item.id) : null"
  >
    <div class="guest-data flex-1 flex flex-row items-center">
<!--      <UIAvatar-->
<!--        :name-length="guest.firstName.length + guest.lastName.length"-->
<!--      />-->
      <AvatarChip
        :title="item.fullName"
        :initials="item.fullName.charAt(0)"
        :name-length="item.fullName.length - 1"
        :size="avatarChipSize"
        :src="item.photoURL"
        color="#f3f3f3"
        :style="{  flex: `0 0 ${this.avatarChipSize}px` }"
        class="mr-25 mobile:mr-15"
      />
      <TransitionGroup
        name="name-vip-animation"
        tag="div"
        class="name-vip-container flex flex-col mr-20 overflow-hidden"
      >
        <div
          key="full-name"
          :title="item.fullName"
          data-hj-suppress
          data-private
          class="full-name font-medium text-primary leading-tight truncate name-vip-animation-item
          mobile:text-base"
        >
          {{ item.fullName }}
        </div>
        <div
          v-if="item.vip"
          key="vip"
          class="name-vip-animation-item self-start font-bold text-xs text-secondary tracking-widest leading-tight py-1
          px-7 mt-1 border border-secondary rounded"
        >
          VIP
        </div>
      </TransitionGroup>
      <div class="status-container small:hidden">
        <p
          :title="guestStatus.name"
          :class="guestStatus.tagClasses"
          class="font-medium text-xs tracking-wider uppercase py-5 rounded"
        >
          {{ guestStatus.name }}
        </p>
      </div>
      <p
        :title="extraGuestsFormatter(item.checkedInExtraGuests, item.totalExtraGuests)"
        class="extra-guests-container text-sm text-center mr-20"
      >
        {{ extraGuestsFormatter(item.checkedInExtraGuests, item.totalExtraGuests) }}
      </p>
      <div class="email-container text-sm overflow-hidden">
        <span
          v-show="item.email"
          :title="item.email"
          data-private
          class="email truncate"
        >
          {{ item.email }}
        </span>
      </div>
    </div>
    <div
      v-if="!hideEdit || !hideDelete"
      v-show="idState.showGuestActions && item.snapshotChange !== 'removed'"
      class="guest-actions touch:hidden"
    >
      <ElButton
        v-if="!hideEdit"
        title="Press to edit this guest"
        circle
        class="edit-guest-button"
      >
        <FontAwesomeIcon
          :icon="['fas', 'pen']"
          class="edit-guest-icon"
        />
      </ElButton>
      <FontAwesomeIcon
        v-if="!hideDelete"
        :icon="['fas', 'trash']"
        title="Press to delete this guest"
        class="delete-guest-icon align-middle text-danger ml-15 cursor-pointer"
        @click.stop="item.snapshotChange === 'removed' ? null : $emit('guest-delete', item.id)"
      />
    </div>
  </div>
</template>

<script>
import { IdState } from 'vue-virtual-scroller';
import AvatarChip from '@/components/AvatarChip';
import { guestStatus } from '@/utils/utilityFunctions';

export default {
  props: {
    avatarChipSize: Number,
    item: Object,
    itemIndex: Number,
    itemHeight: Number,
    hideDelete: {
      type: Boolean,
      default: false,
    },
    hideEdit: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [
    IdState({
      idProp: (vm) => vm.item.id,
    }),
  ],
  components: {
    AvatarChip,
  },
  idState: () => ({ // This replaces data () { ... } so that each item doesn't share it's state
    showGuestActions: false,
    touchDragging: false,
  }),
  computed: {
    guestStatus() {
      return guestStatus[this.item?.status] ?? guestStatus.default;
    },
  },
  methods: {
    extraGuestsFormatter(arrivedExtras, invitedExtras) {
      if (!arrivedExtras && !invitedExtras) return '-';

      return `${arrivedExtras || 0}/${invitedExtras} extra guests`;
    },
    onTouchEnd() {
      if (this.item.snapshotChange !== 'removed' && !this.touchDragging) {
        this.$emit('guest-update', this.item.id);
      }
      this.touchDragging = false;
    },
  },
};
</script>

<style scoped>
.guest-row {
  transition: box-shadow 120ms easeInOutQuart, background-color 300ms easeInOutQuart;

  &.added,
  &.modified,
  &.removed {
    .full-name,
    .status-container .el-tag,
    .extra-guests-container,
    .email-container {
      @apply font-bold;
    }
  }

  &.added {
    @apply bg-guest-change-added;
  }

  &.modified {
    @apply bg-guest-change-modified;
  }

  &.removed {
    @apply bg-guest-change-removed;
  }

  .guest-data {
    .name-vip-container {
      flex: 0 0 240px;
    }

    .status-container {
      @apply text-center mr-20;

      flex: 0 0 105px;
    }

    .extra-guests-container {
      flex: 0 0 160px;
    }

    .email-container {
      flex: 1 1 240px;

      .email {
        @apply block !important;
      }
    }
  }

  .guest-actions {
    @apply ml-25;

    flex: 0 0 auto;

    .edit-guest-button {
      @apply p-10;

      .edit-guest-icon {
        width: 14px;
        height: 14px;
        vertical-align: middle;
      }
    }
  }
}

@screen touch {
  .guest-row .guest-data {
    .name-vip-container {
      flex: 0 0 140px;
    }

    .extra-guests-container {
      @apply mr-0;

      flex: 0 0 160px;
    }

    .email-container {
      @apply mr-0;

      flex: 0 2 100px;
    }
  }
}

@screen mobile {
  .guest-row .guest-data {
    .name-vip-container {
      flex: 0 0 150px;
    }

    .extra-guests-container {
      flex: 0 0 100px;
    }

    .email-container {
      @apply mr-0;

      flex: 0 1 140px;
    }
  }
}

@screen small {
  .guest-row .guest-data {
    .name-vip-container {
      flex: 0 0 100px;
    }
  }
}

@screen any-pointer-fine {
  .guest-row:hover {
    @apply shadow-3dp;
  }
}

/* Transitions */
.name-vip-animation-item {
  transition: all 0.3s ease;
}

.name-vip-animation-enter, .name-vip-animation-leave-to {
  @apply opacity-0;

  transform: translateY(20px);
}

.name-vip-animation-leave-active {
  @apply opacity-0;
  @apply absolute;

  transform: translateY(15px);
}

</style>
